import { Box, Button, SxProps, Theme, Tooltip } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { READOUT_DIM, TRIGGER_CHEVRON_SIZE_PX } from '../chrome';
import { modalityBeside } from './tileViewModel';

/**
 * Widest the name may run before it is cut short with an ellipsis, in CSS
 * px. The modality beside it is never cut.
 */
const NAME_MAX_WIDTH_PX = 140;

interface ImageNameTriggerProps {
    /** Shown before the name unless the name already is the modality. */
    readonly modality?: string;
    /**
     * Series description, or the localised "Unknown Series". PHI: masked
     * where rendered. Undefined while the control names no image yet.
     */
    readonly label: string | undefined;
    /** What the control says while it names no image. Not PHI. */
    readonly placeholder?: string;
    /** Shown before the modality, e.g. how the image is placed. */
    readonly lead?: ReactNode;
    /**
     * More about the image, under its full name above the control on hover
     * and on keyboard focus. It describes the control and never names it,
     * so patient data in it can stay masked while `ariaLabel` stays the
     * name.
     */
    readonly tooltip?: ReactNode;
    readonly ariaLabel: string;
    readonly open: boolean;
    /** Refuses to open the list. */
    readonly disabled?: boolean;
    /**
     * Why the list does not open, when it does not. The control stays,
     * disabled, and the reason shows above it, under the full name, in
     * place of `tooltip`.
     */
    readonly disabledReason?: string;
    readonly onOpen: (anchor: HTMLElement) => void;
    readonly testId: string;
    /** Style laid over the button's own, e.g. to join it to a neighbour. */
    readonly sx?: SxProps<Theme>;
}

/**
 * An image named as the control that changes it: the modality, the name and
 * the chevron that opens the list. A long name is cut short, and the tooltip
 * gives it in full, masked as the name is.
 *
 * Every image a row displays is picked through one of these, so no image
 * reads as fixed while another reads as changeable.
 */
export default function ImageNameTrigger({
    modality,
    label,
    placeholder,
    lead,
    tooltip,
    ariaLabel,
    open,
    disabled = false,
    disabledReason,
    onOpen,
    testId,
    sx,
}: ImageNameTriggerProps) {
    const shownModality =
        label === undefined ? undefined : modalityBeside(modality, label);
    const blocked = disabled || disabledReason !== undefined;
    // The full name leads the tooltip, above what else it says.
    const fullName =
        label === undefined ? undefined : (
            <SensitiveInfoTypography>{label}</SensitiveInfoTypography>
        );
    const title =
        fullName !== undefined && tooltip !== undefined ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Blocks, so the parts are read apart, not run together. */}
                <Box>{fullName}</Box>
                <Box>{tooltip}</Box>
            </Box>
        ) : (
            (fullName ?? tooltip)
        );
    const button = (
        <Button
            variant="picker"
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={blocked}
            data-testid={testId}
            onClick={(event: ReactMouseEvent<HTMLButtonElement>) =>
                onOpen(event.currentTarget)
            }
            endIcon={<ChevronDown size={TRIGGER_CHEVRON_SIZE_PX} />}
            sx={sx}
        >
            {lead !== undefined ? (
                <Box component="span" sx={{ display: 'flex', mr: 0.5 }}>
                    {lead}
                </Box>
            ) : null}
            {shownModality ? (
                <Box
                    component="span"
                    sx={{ color: READOUT_DIM, mr: 1, flexShrink: 0 }}
                >
                    {shownModality}
                </Box>
            ) : null}
            <Box
                component="span"
                data-testid="image-name"
                sx={{
                    maxWidth: NAME_MAX_WIDTH_PX,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {label === undefined ? (
                    placeholder
                ) : (
                    <SensitiveInfoTypography>{label}</SensitiveInfoTypography>
                )}
            </Box>
        </Button>
    );
    // A disabled button fires no events, so a disabled control's full name
    // and reason hang on the hint around it. Both are always there, so a
    // tooltip coming or going never remounts the button and drops its
    // focus; an empty title never opens.
    return (
        <DisabledHint
            title={disabledReason}
            lead={fullName}
            placement="top"
            keepWrapper
            spanStyle={{ display: 'inline-flex' }}
        >
            <Tooltip
                title={blocked ? '' : (title ?? '')}
                placement="top"
                describeChild
            >
                {button}
            </Tooltip>
        </DisabledHint>
    );
}
